import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "@/components/Logo";
import { AREAS, SCALE } from "@/questions";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Questionnaire() {
  const { token } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [department, setDepartment] = useState("");
  const [answers, setAnswers] = useState(Array(65).fill(null));
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Questionario di Benessere Aziendale — ECLENIA";
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      document.head.appendChild(m);
    }
    m.content = "noindex, nofollow";
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/q/${token}`)
      .then(({ data }) => setCampaign(data))
      .catch(() => setInvalid(true));
  }, [token]);

  const answered = answers.filter((a) => a !== null).length;

  const setAnswer = (idx, value) => {
    const next = [...answers];
    next[idx] = value;
    setAnswers(next);
  };

  const submit = async () => {
    if (answered < 65) {
      setError(`Mancano ${65 - answered} risposte per completare il questionario.`);
      const firstEmpty = answers.findIndex((a) => a === null);
      document.getElementById(`q-${firstEmpty}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/q/${token}/submit`, {
        department: department || null,
        answers,
      });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch {
      setError("Invio non riuscito. Riprova tra qualche istante.");
    } finally {
      setSending(false);
    }
  };

  if (invalid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-5" data-testid="questionnaire-invalid">
        <div className="max-w-md text-center">
          <LogoMark theme="light" size={36} />
          <h1 className="mt-6 font-serif text-2xl text-navy-900">Link non valido</h1>
          <p className="mt-3 text-sm leading-relaxed text-navy-900/60">
            Questo link non corrisponde a nessuna campagna attiva. Verifica il link ricevuto o contatta chi te lo ha inviato.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-5 grain relative" data-testid="questionnaire-thanks">
        <div className="max-w-md text-center">
          <CheckCircle2 size={44} className="mx-auto text-sage-700" />
          <h1 className="mt-6 font-serif text-3xl text-navy-900">Grazie per il tuo contributo</h1>
          <p className="mt-4 text-sm leading-relaxed text-navy-900/60">
            Le tue risposte sono state registrate in forma completamente anonima e contribuiranno a costruire un luogo di lavoro più sano.
          </p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return <div className="flex min-h-screen items-center justify-center bg-cream text-sm text-navy-900/50" data-testid="questionnaire-loading">Caricamento…</div>;
  }

  let qIndex = 0;

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="questionnaire-page">
      <header className="border-b border-navy-900/10 bg-cream/95 backdrop-blur">
        <div className="mx-auto max-w-4xl px-5 py-5">
          <div className="flex items-center gap-3">
            <LogoMark theme="light" size={30} />
            <div>
              <p className="font-serif text-lg tracking-tight text-navy-900">ECLENIA</p>
              <p className="text-[9px] uppercase tracking-[0.28em] text-navy-900/50">Corporate Health & Workplace Wellbeing</p>
            </div>
          </div>
          <h1 className="mt-8 font-serif text-3xl tracking-tight text-navy-900 sm:text-4xl" data-testid="questionnaire-title">
            Questionario di Benessere Aziendale
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-navy-900/65">
            Azienda: <span className="font-medium text-navy-900" data-testid="questionnaire-company">{campaign.name}</span> ·
            Le risposte sono completamente anonime: non viene registrato alcun dato personale.
          </p>
          {campaign.departments?.length > 0 && (
            <div className="mt-5">
              <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-navy-900/50">Reparto (facoltativo)</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="border border-navy-900/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold"
                data-testid="questionnaire-department-select"
              >
                <option value="">Seleziona il reparto…</option>
                {campaign.departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          <div className="mt-6 border border-gold/30 bg-white/70 p-4 text-xs leading-relaxed text-navy-900/60" data-testid="scale-legend">
            <span className="font-medium text-navy-900">Scala di valutazione: </span>
            {SCALE.map((s) => `${s.value} = ${s.label}`).join("  ·  ")}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5">
        {AREAS.map((area, ai) => (
          <section key={area.name} className="mt-12" data-testid={`area-section-${ai}`}>
            <p className="flex items-center gap-4 text-sm font-medium uppercase tracking-[0.3em] text-gold-deep">
              <span className="inline-block h-px w-10 bg-gold/70" />
              {String(ai + 1).padStart(2, "0")} · {area.name}
            </p>
            <div className="mt-5 space-y-3">
              {area.questions.map((q) => {
                const idx = qIndex++;
                const current = idx;
                return (
                  <div
                    key={current}
                    id={`q-${current}`}
                    className={`border bg-white p-5 transition-colors ${answers[current] === null ? "border-navy-900/10" : "border-sage-200"}`}
                    data-testid={`question-${current}`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-xl text-sm leading-relaxed text-navy-900">{q}</p>
                      <div className="flex shrink-0 gap-2">
                        {SCALE.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => setAnswer(current, s.value)}
                            title={s.label}
                            data-testid={`question-${current}-option-${s.value}`}
                            className={`flex h-10 w-10 items-center justify-center rounded-full border font-mono text-sm transition-all duration-200 ${
                              answers[current] === s.value
                                ? "border-sage-700 bg-sage-700 text-cream"
                                : "border-navy-900/20 text-navy-900/60 hover:border-sage-700 hover:text-sage-700"
                            }`}
                          >
                            {s.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <div className="mt-14 border-t-2 border-navy-900/10 pt-8 text-center">
          <p className="font-mono text-sm text-navy-900/50" data-testid="questionnaire-progress">{answered} di 65 risposte</p>
          {error && <p className="mt-4 text-sm text-red-700" data-testid="questionnaire-error">{error}</p>}
          <button
            onClick={submit}
            disabled={sending}
            className="mt-6 bg-navy-900 px-10 py-4 text-sm font-medium tracking-wide text-cream transition-colors hover:bg-sage-700 disabled:opacity-60"
            data-testid="questionnaire-submit-btn"
          >
            {sending ? "Invio in corso…" : "Invia il questionario"}
          </button>
        </div>
      </main>
    </div>
  );
}
